import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {Subject} from '@github-ui/comment-box/subject'
import {validateIssueBody} from '@github-ui/entity-validators'
import {IssueBodyEditor} from '@github-ui/issue-body/components/IssueBodyEditor'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {clsx} from 'clsx'
import {useId, useMemo, useState, type RefObject} from 'react'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'
import {FormControl} from '@primer/react'
import {Stack} from '@primer/react/experimental'

import {useUpdateDescriptionMutation} from '../../mutations/use-update-description-mutation'
import styles from './DescriptionEditor.module.css'
import {
  useLayoutBaseRepository,
  useLayoutPullRequestId,
  useLayoutPullRequestNumber,
  useLayoutPullRequestTitle,
} from '../../stores/selectors'

// eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- static string literal is safe
const PLACEHOLDER_TEXT = 'No description provided.' as SafeHTMLString
const DESCRIPTION_SAVE_ERROR = 'Failed to update the description.'
const DESCRIPTION_STALE_ERROR =
  'The description was updated by someone else. Cancel to see the latest version, then reopen to continue editing.'

/** Session storage key for persisting an unsaved PR description draft. */
const prDescriptionDraftKey = (pullRequestId: number) => `pr-description-draft-${pullRequestId}`

type DescriptionEditorProps = {
  /** Current description as sanitized HTML, or null/undefined if none. Used by the read-only viewer. */
  bodyHtml: SafeHTMLString | null | undefined
  /** Current description as raw markdown. Used as the initial draft when editing. */
  body: string
  isEditing: boolean
  onEditingChange: (isEditing: boolean) => void
  /** Class for the viewer container; ignored when editing. */
  viewerClassName?: string
  /** Element to receive focus when the editor is dismissed (e.g. the edit trigger). */
  returnFocusRef?: RefObject<HTMLElement | null>
}

/**
 * Shared description display + editing experience for pull requests. Handles
 * toggling between a `NewMarkdownViewer` and a `CommentBox` editor, draft
 * persistence, stale-content detection, and saving via `useUpdateDescriptionMutation`.
 *
 * The parent owns the `isEditing` state so it can place the edit trigger
 * wherever it makes sense (e.g. an overview kebab menu, a panel title bar).
 * While editing, the trigger should remain mounted but marked `inactive`.
 */
export function DescriptionEditor({
  bodyHtml,
  body,
  isEditing,
  onEditingChange,
  viewerClassName,
  returnFocusRef,
}: DescriptionEditorProps) {
  const pullRequestNumber = useLayoutPullRequestNumber()
  const pullRequestId = useLayoutPullRequestId()
  const pullRequestTitle = useLayoutPullRequestTitle()
  const baseRepository = useLayoutBaseRepository()

  const currentBodyHtml = bodyHtml ?? ''
  const isEmptyDescription = currentBodyHtml === ''

  const [sessionDraft, setSessionDraft, resetSessionDraft] = useSessionStorage<string | undefined>(
    prDescriptionDraftKey(pullRequestId),
    undefined,
  )
  const [draft, setDraft] = useState(() => sessionDraft ?? body)
  const [bodyIsStale, setBodyIsStale] = useState(false)
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | undefined>(undefined)
  const saveErrorMessageId = useId()

  const [prevBody, setPrevBody] = useState(body)
  if (body !== prevBody) {
    setPrevBody(body)
    if (isEditing) {
      setBodyIsStale(true)
      setSaveErrorMessage(DESCRIPTION_STALE_ERROR)
    } else {
      setDraft(sessionDraft ?? body)
    }
  }

  const [prevIsEditing, setPrevIsEditing] = useState(isEditing)
  if (isEditing !== prevIsEditing) {
    setPrevIsEditing(isEditing)
    if (isEditing) {
      setBodyIsStale(false)
      if (sessionDraft !== undefined) {
        setDraft(sessionDraft)
      } else {
        setDraft(body)
      }
    }
  }

  const repoVisibility = baseRepository.public ? 'PUBLIC' : 'PRIVATE'
  const shouldShowCopilot = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: repoVisibility,
    userCanPushToRepo: baseRepository.currentUserCanPush ?? false,
  })

  const handleDraftChange = (value: string) => {
    setDraft(value)
    setSessionDraft(value)
  }

  const closeEditor = () => {
    onEditingChange(false)
    setSaveErrorMessage(undefined)
    setBodyIsStale(false)
    if (returnFocusRef) {
      requestAnimationFrame(() => returnFocusRef.current?.focus())
    }
  }

  const subject = useMemo<Subject>(
    () => ({
      type: 'pull_request',
      id: {id: pullRequestId.toString(), databaseId: pullRequestId},
      pullRequestNumber,
      repository: {
        databaseId: baseRepository.id,
        nwo: `${baseRepository.ownerLogin}/${baseRepository.name}`,
        slashCommandsEnabled: false,
      },
    }),
    [pullRequestId, pullRequestNumber, baseRepository],
  )

  const validationResult = useMemo(() => validateIssueBody(draft), [draft])

  const {mutateAsync: updateDescription, isPending} = useUpdateDescriptionMutation(error => {
    if (error.cause === 409) {
      setBodyIsStale(true)
      setSaveErrorMessage(DESCRIPTION_STALE_ERROR)
    } else {
      setSaveErrorMessage(DESCRIPTION_SAVE_ERROR)
    }
  })

  const handleSave = async () => {
    if (!validationResult.isValid) return
    setSaveErrorMessage(undefined)
    try {
      await updateDescription({id: pullRequestId, body: draft})
      resetSessionDraft()
      closeEditor()
    } catch {
      // Error already handled by onError callback in useUpdateDescriptionMutation
    }
  }

  const onCancel = () => {
    if (!bodyIsStale) {
      resetSessionDraft()
    }
    setDraft(body)
    closeEditor()
  }

  if (isEditing) {
    return (
      <div className={styles.editor}>
        <Stack direction="vertical" gap="condensed">
          <IssueBodyEditor
            editorDisabled={isPending}
            subjectId={pullRequestId.toString()}
            subject={subject}
            body={draft}
            bodyIsStale={bodyIsStale}
            onChange={handleDraftChange}
            onCancel={onCancel}
            onCommit={handleSave}
            repoOwner={baseRepository.ownerLogin}
            repoName={baseRepository.name}
            title={pullRequestTitle}
            shouldShowCopilotFunctionality={shouldShowCopilot}
          />
          {saveErrorMessage && (
            <div id={saveErrorMessageId} role="alert">
              <FormControl.Validation variant="error">{saveErrorMessage}</FormControl.Validation>
            </div>
          )}
        </Stack>
      </div>
    )
  }

  return (
    <NewMarkdownViewer
      verifiedHTML={isEmptyDescription ? PLACEHOLDER_TEXT : currentBodyHtml}
      markdownValue={body}
      className={clsx(viewerClassName, isEmptyDescription && 'fgColor-muted text-italic')}
    />
  )
}
